import { ComponentPropsWithoutRef, ReactNode } from "react";
import Image, { type ImageProps } from "next/legacy/image";
import { Tab } from "@headlessui/react";
import featureImage1 from "public/marketing/sponsors/secondary_features/features_1.png";
import featureImage2 from "public/marketing/sponsors/secondary_features/features_2.png";
import featureImage3 from "public/marketing/sponsors/secondary_features/features_3.jpg";
import secondaryFeaturesUser from "public/marketing/sponsors/secondary_features/user.svg";
import secondaryFeaturesAnalytics from "public/marketing/sponsors/secondary_features/analytics.svg";
import secondaryFeaturesTemplates from "public/marketing/sponsors/secondary_features/templates.svg";
import { classNames } from "www/shared/utils";
import Container from "www/shared/components/container/Container";

interface FeatureItem {
  name: ReactNode;
  summary: string;
  description: string;
  image: ImageProps["src"];
  icon: ImageProps["src"];
}

const features: FeatureItem[] = [
  {
    name: "Contacts Management",
    summary:
      "Contacts get automatically added as new investors interact with your deal",
    description:
      "No more worrying about lost connections. New referrals get automatically added into your contact book, available for your next deal. Sit back and relax as your address book organically grows.",
    image: featureImage1,
    icon: secondaryFeaturesUser,
  },
  {
    name: "Analytics",
    summary: "Have a crystal clear understanding of your deal's engagement",
    description:
      "Analyze investors engagement across emails and their underlying deals -- enabling you to understand who's engaged and who's not. Easily send follow-up emails based on engagement.",
    image: featureImage2,
    icon: secondaryFeaturesAnalytics,
  },
  {
    name: "Templates",
    summary:
      "Spend less time designing emails, more time building quality relationships",
    description:
      "Optimize your email's visual appeal and engagement starting on day 1. Choose among our beautiful and battle-tested email templates, purpose built for syndicators.",
    image: featureImage3,
    icon: secondaryFeaturesTemplates,
  },
];

const SponsorsSecondaryFeatures = () => {
  return (
    <section className="py-16 lg:py-28">
      <Container>
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-gray-900 text-3xl sm:text-4xl lg:text-5xl font-semibold">
            Give yourself email superpowers
          </h2>
          <p className="text-gray-600 text-lg mt-5">
            Use an email campaign service tailor-built for real estate
            syndicators.
          </p>
        </div>
        <FeaturesMobile />
        <FeaturesDesktop />
      </Container>
    </section>
  );
};

const Feature = ({
  feature,
  isActive,
  className,
  ...props
}: ComponentPropsWithoutRef<"div"> & {
  feature: FeatureItem;
  isActive: boolean;
}) => {
  return (
    <div
      className={classNames(
        "transition-colors",
        className,
        isActive ? "text-gray-800" : "text-gray-600 hover:text-gray-800 group"
      )}
      {...props}
    >
      <div
        className={classNames(
          "w-9 h-9 rounded-lg flex items-center justify-center transition-colors",
          isActive ? "bg-green-700" : "bg-gray-600 group-hover:bg-gray-800"
        )}
      >
        <Image
          className="w-full"
          src={feature.icon}
          alt=""
          width={36}
          height={36}
        />
      </div>
      <h3
        className={classNames(
          isActive ? "text-green-700" : "",
          "mt-6 text-sm font-medium"
        )}
      >
        {feature.name}
      </h3>
      <p className="mt-2 text-xl">{feature.summary}</p>
      <p className="mt-5 lg:mt-6 text-sm ">{feature.description}</p>
    </div>
  );
};

const FeaturesMobile = () => {
  return (
    <div className="-mx-4 mt-14 lg:mt-20 flex flex-col gap-y-10 overflow-hidden px-4 sm:-mx-6 sm:px-6 lg:hidden">
      {features.map((feature) => (
        <div key={feature.summary}>
          <Feature className="mx-auto max-w-2xl" feature={feature} isActive />
          <div className="relative mt-10 pb-10">
            <div className="absolute -inset-x-4 bottom-0 top-8 bg-slate-200 sm:-inset-x-6" />
            <div className="relative mx-auto lg:w-[52.75rem] overflow-hidden rounded-xl bg-white shadow-lg shadow-slate-800/5 ring-1 ring-slate-500/10">
              <Image
                className="w-full"
                src={feature.image}
                alt=""
                sizes="52.75rem"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const FeaturesDesktop = () => {
  return (
    <Tab.Group className="hidden lg:mt-16 lg:block" as="div">
      {({ selectedIndex }) => (
        <>
          <Tab.List className="grid grid-cols-3 gap-x-8">
            {features.map((feature, featureIndex) => (
              <Feature
                className="relative"
                feature={{
                  ...feature,
                  name: (
                    <Tab className="outline-none">
                      <span className="absolute inset-0" />
                      {feature.name}
                    </Tab>
                  ),
                }}
                isActive={featureIndex === selectedIndex}
                key={feature.summary}
              />
            ))}
          </Tab.List>
          <Tab.Panels className="relative mt-12 overflow-hidden rounded-[32px] bg-slate-200 px-14 py-16 xl:px-16">
            <div className="flex">
              {features.map((feature, featureIndex) => (
                <Tab.Panel
                  className={classNames(
                    "px-5 transition duration-500 ease-in-out outline-none",
                    featureIndex !== selectedIndex && "opacity-60"
                  )}
                  static
                  style={{ transform: `translateX(-${selectedIndex * 100}%)` }}
                  aria-hidden={featureIndex !== selectedIndex}
                  key={feature.summary}
                >
                  <div className="w-[52.75rem] overflow-hidden rounded-xl bg-white shadow-lg shadow-slate-800/5 ring-1 ring-slate-500/10">
                    <Image
                      className="w-full object-cover"
                      src={feature.image}
                      alt=""
                      sizes="52.75rem"
                    />
                  </div>
                </Tab.Panel>
              ))}
            </div>
            <div className="pointer-events-none absolute inset-0 rounded-4xl ring-1 ring-inset ring-slate-800/10" />
          </Tab.Panels>
        </>
      )}
    </Tab.Group>
  );
};

export default SponsorsSecondaryFeatures;
